import clsx from "clsx";

import { AlertSuccess24, TrendingUp } from "@hagerty/icons";
import { unstable__Button as Button } from "@hagerty/react-components";

import { Card } from "../../../Card/Card";
import { BillDue } from "../billDue/BillDue";
import { formatCurrency } from "../utils/formatCurrency";

import {
  ApiBill,
  BillProps,
  BillStatus,
  BillType,
  BillCategory,
} from "./Bill.types";
import styles from "./Bill.module.scss";
import { Skeleton } from "./skeleton/Skeleton";

const getProductName = (
  productCategory: ApiBill["category"],
  productDetails: ApiBill["productDetails"],
  productNumber: ApiBill["productNumber"]
) => {
  if (productCategory === "HDC1DOT0") {
    return "Drivers Club 1.0 membership";
  } else if (productCategory === "HDC1DOT5") {
    return "Drivers Club membership";
  } else if (productDetails && productDetails.length === 0) {
    return productNumber ? `Policy #${productNumber}` : "Policy";
  } else {
    return `${productDetails[0]}${
      productDetails && productDetails.length > 1
        ? ` +${productDetails.length - 1}`
        : ""
    }`;
  }
};

const getPaymentPlan = (
  productCategory: BillCategory,
  isRecursive: boolean
) => {
  if (productCategory === "HDC1DOT5") {
    return "Annual Payment Plan";
  }

  if (productCategory === "HDC1DOT0") {
    return isRecursive ? "Monthly Payment Plan" : "Annual premium";
  }

  return null;
};

export const Bill = ({
  bill,
  className,
  style,
  returnButtonLabel,
  returnUrl,
  onPayClick,
  onViewDetailsClick,
  onAction,
}: BillProps) => {
  const {
    type,
    category,
    productNumber,
    nextPaymentDate,
    productDetails,
    isSubmitted,
    paymentUrl,
    alignedBill,
    total,
  } = bill;

  const isAutoPay = type === BillType.AutoPay;
  const name = getProductName(category, productDetails, productNumber);

  let updatedPaymentUrl = paymentUrl;

  if (!onPayClick && !isAutoPay) {
    if (returnUrl) {
      const params = new URLSearchParams({
        returnUrl: returnUrl,
        ...(returnButtonLabel ? { returnButtonLabel } : undefined),
      });

      updatedPaymentUrl = paymentUrl + "?" + params.toString();
    }
  }

  const viewDetailsProps = onViewDetailsClick
    ? {
        onClick: () => {
          onViewDetailsClick(bill);
          onAction?.("details", bill);
        },
      }
    : {
        as: "a" as const,
        href: updatedPaymentUrl,
        onClick: () => onAction?.("details", bill),
      };

  const payNowProps = onPayClick
    ? {
        onClick: () => {
          onPayClick(bill);
          onAction?.("pay", bill);
        },
      }
    : {
        as: "a" as const,
        href: updatedPaymentUrl,
        onClick: () => onAction?.("pay", bill),
      };

  const productDescription = alignedBill
    ? getProductName(alignedBill.category, [], alignedBill.productNumber)
    : getPaymentPlan(category, isAutoPay);

  const viewDetailsButtonLabel = `Autopay ${
    bill.status === BillStatus.Failed ? "failed " : ""
  }for ${name} on ${nextPaymentDate}. Outstanding amount is ${
    total.formattedAmount
  }. View details`;

  const payButtonLabel = `${
    bill.status === BillStatus.PastDue ? "Past due " : ""
  }bill for ${name}. Pay ${total.formattedAmount} now.`;

  return (
    <Card
      as="article"
      aria-label="bill"
      className={clsx(styles.card, className)}
      style={style}
    >
      <Card.Title className={styles.title}>
        {formatCurrency(total.amount / 100)}
      </Card.Title>
      {nextPaymentDate && (
        <BillDue
          date={nextPaymentDate}
          variant={isAutoPay ? "recursive" : "single"}
          hasError={bill.informAboutStatus && bill.status !== BillStatus.Ok}
        />
      )}
      <Card.Separator />
      <div>{name}</div>
      {productDescription && (
        <>
          <Card.Separator />
          <div>{productDescription}</div>
        </>
      )}
      {bill.revalOffer && (
        <>
          <Card.Separator />
          <p className={styles.revaluationLabel}>
            <TrendingUp /> New vehicle value(s) available
          </p>
        </>
      )}
      <Card.Footer as="footer">
        {isSubmitted && (
          <p className={styles.paymentSubmitted}>
            <AlertSuccess24 /> Payment submitted
          </p>
        )}
        {!isSubmitted && isAutoPay && (
          <Button
            variant="outline"
            size="medium"
            {...viewDetailsProps}
            aria-label={viewDetailsButtonLabel}
          >
            View details
          </Button>
        )}
        {!isSubmitted && !isAutoPay && (
          <Button
            variant="solid"
            size="medium"
            {...payNowProps}
            aria-label={payButtonLabel}
          >
            {bill.revalOffer ? "Review and pay" : "Pay now"}
          </Button>
        )}
      </Card.Footer>
    </Card>
  );
};

Bill.Skeleton = Skeleton;
